import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { copy } from "../copy";
import Icon, { type IconName } from "../components/Icon";
import SiteFooter from "../components/SiteFooter";
import type { Result } from "../dashboard/api";
import { fullPageLoad } from "../navigate";
import { useSession, useSessionReady } from "../session";
import {
  adminApi,
  EMPTY_FILTERS,
  type AdminContest,
  type AdminUser,
  type AdminWithdrawal,
  type Options,
  type Goals,
  type Platform,
  type Queue,
  type QueueFilters,
  type Usage,
  type WithdrawalState,
} from "../admin/api";
import ContestRow, { type RowActions } from "../admin/ContestRow";
import FilterRail from "../admin/FilterRail";
import { GoalsPanel, MarkPaidDialog, PlatformTable, UsagePanel, UsersTable, WithdrawalsTable, WonTable } from "../admin/Tables";
import { ConfirmDialog, Empty, inputClass, ghostButton, LoadError, Loading, primaryButton, type Load } from "../admin/ui";

export const ADMIN_LOGIN = `/login?next=${encodeURIComponent("/admin")}`;
export const NEW_CONTEST_PATH = "/admin/contests/new";

type Tab = Queue | "users" | "withdrawals" | "platform" | "usage" | "goals";
const TABS: { key: Tab; icon: IconName }[] = [
  { key: "ready", icon: "trophy" },
  { key: "votes", icon: "barChart" },
  { key: "review", icon: "searchEye" },
  { key: "users", icon: "team" },
  { key: "won", icon: "star" },
  { key: "withdrawals", icon: "wallet" },
  { key: "platform", icon: "coins" },
  { key: "usage", icon: "lineChart" },
  { key: "goals", icon: "handCoin" },
];
const QUEUES: Queue[] = ["ready", "votes", "review", "won"];
const isQueue = (tab: Tab): tab is Queue => (QUEUES as string[]).includes(tab);

type Paged<T> = { items: T[]; total: number; page: number };
type Notice = { kind: "error" | "ok"; text: string } | null;
type Confirm = { message: string; go: () => void } | null;

const resultText = (key: string, name = ""): string => {
  const r = copy.admin.results[key];
  return typeof r === "function" ? r(name) : (r ?? "");
};

// The Control Room. Admin-only on the server; here a visitor or member sees a
// plain message and no /v1/admin request is made.
export default function Admin() {
  return (
    <AdminGate title={copy.admin.title}>
      <ControlRoom />
    </AdminGate>
  );
}

// AdminGate renders children only for an admin session. Everyone else gets
// the admins-only panel, so no /v1/admin request is made for them.
export function AdminGate({ title, children }: { title: string; children: ReactNode }) {
  const session = useSession();
  const ready = useSessionReady();
  const t = copy.admin;

  useEffect(() => {
    document.title = title;
  }, [title]);

  if (!ready) return <Shell><Loading /></Shell>;
  if (session.kind !== "admin") {
    return (
      <Shell>
        <div data-testid="admins-only" className="mx-auto max-w-lg rounded-2xl border border-ground-edge bg-ground-raised p-10 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-ground-edge text-2xl text-gold">
            <Icon name="lock" />
          </span>
          <h1 className="mt-5 font-headline text-3xl uppercase text-chrome">{t.onlyHeading}</h1>
          <p className="mt-3 text-sm text-chrome-dim">{t.onlyBody}</p>
          {session.kind === "visitor" && (
            <a href={ADMIN_LOGIN} className="bf-focusable mt-6 inline-block text-[11px] font-bold uppercase tracking-[0.14em] text-money">
              {t.signIn}
            </a>
          )}
        </div>
      </Shell>
    );
  }
  return <Shell>{children}</Shell>;
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen overflow-x-hidden bg-ground font-sans text-chrome antialiased">
      <main className="px-4 pb-20 pt-24 md:px-6 md:pb-28 md:pt-32">
        <div className="mx-auto w-full max-w-[1360px]">{children}</div>
      </main>
      <SiteFooter />
    </div>
  );
}

function ControlRoom() {
  const t = copy.admin;
  const [tab, setTab] = useState<Tab>("ready");
  const [filters, setFilters] = useState<QueueFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<QueueFilters>(EMPTY_FILTERS);
  const [options, setOptions] = useState<Options | null>(null);
  const [queues, setQueues] = useState<Record<Queue, Load<Paged<AdminContest>>>>({
    ready: { status: "idle" },
    votes: { status: "idle" },
    review: { status: "idle" },
    won: { status: "idle" },
  });
  const [q, setQ] = useState("");
  const [users, setUsers] = useState<Load<Paged<AdminUser> & { q: string }>>({ status: "idle" });
  const [wState, setWState] = useState<WithdrawalState>("pending");
  const [withdrawals, setWithdrawals] = useState<Load<AdminWithdrawal[]>>({ status: "idle" });
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [platform, setPlatform] = useState<Load<Platform>>({ status: "idle" });
  const [usage, setUsage] = useState<Load<Usage>>({ status: "idle" });
  const [goals, setGoals] = useState<Load<Goals>>({ status: "idle" });
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  // The withdrawal whose Mark paid dialog is open: paying asks for the fee,
  // so it has its own dialog rather than the plain confirm.
  const [payFor, setPayFor] = useState<AdminWithdrawal | null>(null);

  const loadQueue = useCallback(async (queue: Queue, f: QueueFilters, page = 1) => {
    if (page === 1) setQueues((s) => ({ ...s, [queue]: { status: "loading" } }));
    const res = await adminApi.queue(queue, f, page);
    setQueues((s) => {
      if (!res.ok) return page === 1 ? { ...s, [queue]: { status: "error" } } : s;
      const prev = s[queue];
      const items = page > 1 && prev.status === "ready" ? [...prev.data.items, ...res.data.contests] : res.data.contests;
      return { ...s, [queue]: { status: "ready", data: { items, total: res.data.total, page } } };
    });
  }, []);

  const loadUsers = useCallback(async (query: string, page = 1) => {
    if (page === 1) setUsers({ status: "loading" });
    const res = await adminApi.users(query, page);
    setUsers((s) => {
      if (!res.ok) return page === 1 ? { status: "error" } : s;
      const items = page > 1 && s.status === "ready" ? [...s.data.items, ...res.data.users] : res.data.users;
      return { status: "ready", data: { items, total: res.data.total, page, q: query } };
    });
  }, []);

  const loadWithdrawals = useCallback(async (state: WithdrawalState) => {
    setWithdrawals({ status: "loading" });
    const res = await adminApi.withdrawals(state);
    if (!res.ok) {
      setWithdrawals({ status: "error" });
      return;
    }
    setWithdrawals({ status: "ready", data: res.data });
    if (state === "pending") setPendingCount(res.data.length);
  }, []);

  const loadPlatform = useCallback(async () => {
    setPlatform({ status: "loading" });
    const res = await adminApi.platform();
    setPlatform(res.ok ? { status: "ready", data: res.data } : { status: "error" });
  }, []);

  const loadUsage = useCallback(async () => {
    setUsage({ status: "loading" });
    const res = await adminApi.usage();
    setUsage(res.ok ? { status: "ready", data: res.data } : { status: "error" });
  }, []);

  const loadGoals = useCallback(async () => {
    setGoals({ status: "loading" });
    const res = await adminApi.goals();
    setGoals(res.ok ? { status: "ready", data: res.data } : { status: "error" });
  }, []);

  // The header counts need the three judging queues, users, and pending
  // withdrawals; the rest loads when its tab opens.
  useEffect(() => {
    void loadQueue("ready", EMPTY_FILTERS);
    void loadQueue("votes", EMPTY_FILTERS);
    void loadQueue("review", EMPTY_FILTERS);
    void loadUsers("");
    void loadWithdrawals("pending");
    void adminApi.options().then((res) => res.ok && setOptions(res.data));
  }, [loadQueue, loadUsers, loadWithdrawals]);

  useEffect(() => {
    if (tab === "won" && queues.won.status === "idle") void loadQueue("won", applied);
    if (tab === "platform" && platform.status === "idle") void loadPlatform();
    if (tab === "usage" && usage.status === "idle") void loadUsage();
    if (tab === "goals" && goals.status === "idle") void loadGoals();
  }, [tab, queues.won.status, platform.status, usage.status, goals.status, applied, loadQueue, loadPlatform, loadUsage, loadGoals]);

  const reloadQueues = (f: QueueFilters) => {
    for (const queue of QUEUES) {
      if (queue !== "won" || queues.won.status !== "idle") void loadQueue(queue, f);
    }
  };

  // act runs one admin call at a time: every action button is disabled while
  // it is out, and nothing on the page changes until the server answers.
  const act = async <T,>(call: () => Promise<Result<T>>, onOk: (data: T) => void) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const res = await call();
    setBusy(false);
    if (!res.ok) {
      setNotice({ kind: "error", text: res.error });
      return;
    }
    onOk(res.data);
  };
  const ask = (message: string, go: () => void) => setConfirm({ message, go });

  const updateContest = (id: string, patch: Partial<AdminContest>) =>
    setQueues((s) => {
      const next = { ...s };
      for (const queue of QUEUES) {
        const l = s[queue];
        if (l.status === "ready") {
          next[queue] = { ...l, data: { ...l.data, items: l.data.items.map((c) => (c.id === id ? { ...c, ...patch } : c)) } };
        }
      }
      return next;
    });

  const rowActions: RowActions = {
    busy,
    selectWinner: (contest, userID) => {
      const name = contest.candidates.find((c) => c.user_id === userID)?.username ?? "";
      ask(t.confirmWinner(name, contest.title), () =>
        void act(
          () => adminApi.selectWinner(contest.id, userID),
          (r) => {
            setNotice({ kind: "ok", text: resultText(r.result, r.winner_username) });
            reloadQueues(applied);
          },
        ),
      );
    },
    notifyVote: (contest) =>
      void act(
        () => adminApi.notifyVote(contest.id),
        (r) => {
          const sent = typeof r.vote_emails_sent_at === "string" ? r.vote_emails_sent_at : undefined;
          updateContest(contest.id, { vote_emails_sent_at: sent });
          setNotice({ kind: "ok", text: resultText("notified") });
        },
      ),
    updateTallies: (contest) =>
      void act(
        () => adminApi.tallies(contest.id),
        () => {
          setNotice({ kind: "ok", text: resultText("tallies") });
          reloadQueues(applied);
        },
      ),
    setPublished: (contest, published) =>
      void act(
        () => adminApi.publish(contest.id, published),
        () => {
          updateContest(contest.id, { published });
          setNotice({ kind: "ok", text: resultText(published ? "published" : "unpublished") });
        },
      ),
    remove: (contest) =>
      ask(t.confirmDelete(contest.title), () =>
        void act(
          () => adminApi.deleteContest(contest.id),
          () => {
            setNotice({ kind: "ok", text: resultText("deleted") });
            reloadQueues(applied);
          },
        ),
      ),
  };

  const replaceUser = (u: AdminUser) =>
    setUsers((s) => (s.status === "ready" ? { ...s, data: { ...s.data, items: s.data.items.map((x) => (x.id === u.id ? u : x)) } } : s));

  const replaceWithdrawal = (w: AdminWithdrawal) =>
    setWithdrawals((s) => {
      if (s.status !== "ready") return s;
      const items = s.data.map((x) => (x.id === w.id ? w : x));
      if (wState === "pending") setPendingCount(items.filter((x) => x.state === "pending").length);
      return { status: "ready", data: items };
    });

  const total = (l: Load<Paged<unknown>>) => (l.status === "ready" ? l.data.total : null);
  const counts: Partial<Record<Tab, number | null>> = {
    ready: total(queues.ready),
    votes: total(queues.votes),
    review: total(queues.review),
    users: total(users),
    withdrawals: pendingCount,
  };
  const queued = (total(queues.ready) ?? 0) + (total(queues.votes) ?? 0) + (total(queues.review) ?? 0);

  const panel = () => {
    if (isQueue(tab)) {
      const l = queues[tab];
      if (l.status === "error") return <LoadError onRetry={() => loadQueue(tab, applied)} />;
      if (l.status !== "ready") return <Loading />;
      if (l.data.items.length === 0) return <Empty icon="filter" title={t.emptyQueue} sub={t.emptyQueueSub} />;
      return (
        <>
          {tab === "won" ? (
            <WonTable rows={l.data.items} />
          ) : (
            l.data.items.map((c) => <ContestRow key={c.id} contest={c} queue={tab} actions={rowActions} />)
          )}
          {l.data.items.length < l.data.total && (
            <MoreButton onClick={() => loadQueue(tab, applied, l.data.page + 1)} />
          )}
        </>
      );
    }
    if (tab === "users") {
      return (
        <>
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              void loadUsers(q.trim());
            }}
            className="mb-5 flex gap-2"
          >
            <input
              aria-label={t.users.search}
              placeholder={t.users.searchPlaceholder}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className={inputClass}
            />
            <button type="submit" className={ghostButton}>
              {t.filters.search}
            </button>
          </form>
          {users.status === "error" && <LoadError onRetry={() => loadUsers(q.trim())} />}
          {(users.status === "loading" || users.status === "idle") && <Loading />}
          {users.status === "ready" && (
            <>
              <UsersTable
                users={users.data.items}
                busy={busy}
                onLogin={(u) =>
                  ask(t.confirmLogin(u.username), () => void act(() => adminApi.impersonate(u.id), () => fullPageLoad("/dashboard")))
                }
                onDeactivate={(u) =>
                  ask(t.confirmDeactivate(u.username), () =>
                    void act(
                      () => adminApi.deactivate(u.id),
                      (updated) => {
                        replaceUser(updated);
                        setNotice({ kind: "ok", text: resultText("deactivated", updated.username) });
                      },
                    ),
                  )
                }
                onReactivate={(u) =>
                  void act(
                    () => adminApi.reactivate(u.id),
                    (updated) => {
                      replaceUser(updated);
                      setNotice({ kind: "ok", text: resultText("reactivated", updated.username) });
                    },
                  )
                }
              />
              {users.data.items.length < users.data.total && (
                <MoreButton onClick={() => loadUsers(users.data.q, users.data.page + 1)} />
              )}
            </>
          )}
        </>
      );
    }
    if (tab === "withdrawals") {
      return (
        <>
          <div role="radiogroup" aria-label={t.withdrawals.filter} className="mb-5 flex flex-wrap gap-2">
            {(["pending", "completed", "rejected"] as WithdrawalState[]).map((s) => (
              <button
                key={s}
                type="button"
                role="radio"
                aria-checked={wState === s}
                onClick={() => {
                  setWState(s);
                  void loadWithdrawals(s);
                }}
                className={`bf-focusable rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] ${
                  wState === s ? "bg-gold text-ground" : "border border-ground-edge text-chrome-dim hover:text-chrome"
                }`}
              >
                {t.withdrawals.states[s]}
              </button>
            ))}
          </div>
          {withdrawals.status === "error" && <LoadError onRetry={() => loadWithdrawals(wState)} />}
          {(withdrawals.status === "loading" || withdrawals.status === "idle") && <Loading />}
          {withdrawals.status === "ready" && (
            <WithdrawalsTable
              rows={withdrawals.data}
              state={wState}
              busy={busy}
              onPaid={(w) => setPayFor(w)}
              onReject={(w) =>
                ask(t.confirmReject(w.amount_display, w.username), () =>
                  void act(
                    () => adminApi.reject(w.id),
                    (r) => {
                      replaceWithdrawal(r.withdrawal);
                      setNotice({ kind: "ok", text: resultText(r.result) });
                    },
                  ),
                )
              }
            />
          )}
        </>
      );
    }
    if (tab === "platform") {
      if (platform.status === "error") return <LoadError onRetry={loadPlatform} />;
      if (platform.status !== "ready") return <Loading />;
      return <PlatformTable platform={platform.data} />;
    }
    if (tab === "goals") {
      if (goals.status === "error") return <LoadError onRetry={loadGoals} />;
      if (goals.status !== "ready") return <Loading />;
      return <GoalsPanel goals={goals.data} />;
    }
    if (usage.status === "error") return <LoadError onRetry={loadUsage} />;
    if (usage.status !== "ready") return <Loading />;
    return <UsagePanel usage={usage.data} />;
  };

  const tiles: { key: Queue; tone: string }[] = [
    { key: "ready", tone: "text-gold" },
    { key: "votes", tone: "text-chrome" },
    { key: "review", tone: "text-money" },
  ];
  const activeQueue = isQueue(tab) ? queues[tab] : null;

  return (
    <>
      <header className="flex flex-wrap items-end justify-between gap-6 border-b border-ground-edge pb-8 md:pb-10">
        <div>
          <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-gold">
            <Icon name="userStar" className="text-sm" />
            {t.eyebrow}
          </p>
          <h1 className="mt-3 font-headline text-4xl uppercase leading-[0.95] tracking-[-0.02em] text-chrome md:text-5xl">
            {t.heading}
          </h1>
          <p data-testid="admin-summary" className="mt-3 text-[11px] uppercase tracking-[0.16em] text-chrome-dim">
            {t.summary(queued, total(users) ?? 0, pendingCount ?? 0)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Link to={NEW_CONTEST_PATH} className={primaryButton}>
            <Icon name="add" className="text-sm" />
            {t.newContestLink}
          </Link>
          {tiles.map(({ key, tone }) => (
            <div key={key} className="rounded-2xl border border-ground-edge bg-ground-raised px-5 py-3">
              <p className="text-[10px] uppercase tracking-[0.16em] text-chrome-dim">{t.tiles[key as keyof typeof t.tiles]}</p>
              <p data-testid={`tile-${key}`} className={`bf-tabular font-headline text-2xl leading-none ${tone}`}>
                {total(queues[key]) ?? "–"}
              </p>
            </div>
          ))}
        </div>
      </header>

      <div
        role="tablist"
        aria-label={t.sections}
        className="bf-no-scrollbar mt-7 flex gap-1.5 overflow-x-auto rounded-full border border-ground-edge bg-ground-raised p-1.5"
      >
        {TABS.map(({ key, icon }) => {
          const active = tab === key;
          const count = counts[key];
          return (
            <button
              key={key}
              type="button"
              role="tab"
              id={`admin-tab-${key}`}
              aria-selected={active}
              aria-controls="admin-panel"
              onClick={() => setTab(key)}
              className={`bf-focusable flex shrink-0 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                active ? "bg-money text-ground" : "text-chrome-dim hover:text-chrome"
              }`}
            >
              <Icon name={icon} className="text-sm" />
              {t.tabs[key]}
              {count !== undefined && count !== null && (
                <span className={`bf-tabular ${active ? "text-ground/80" : "text-gold"}`}>({count})</span>
              )}
            </button>
          );
        })}
      </div>

      {notice && (
        <div
          role={notice.kind === "error" ? "alert" : "status"}
          className={`mt-6 flex items-start justify-between gap-4 rounded-xl border px-4 py-3 text-[13px] ${
            notice.kind === "error" ? "border-alert/50 bg-alert/10 text-chrome" : "border-money/40 bg-money/10 text-chrome"
          }`}
        >
          <span>{notice.text}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label={t.dismiss} className="bf-focusable text-chrome-dim hover:text-chrome">
            <Icon name="close" />
          </button>
        </div>
      )}

      <div className={`mt-7 grid grid-cols-1 gap-6 ${isQueue(tab) ? "lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-8" : ""}`}>
        {isQueue(tab) && (
          <div className="lg:sticky lg:top-24 lg:self-start">
            <FilterRail
              filters={filters}
              options={options}
              found={activeQueue?.status === "ready" ? activeQueue.data.total : null}
              onChange={setFilters}
              onSearch={() => {
                setApplied(filters);
                reloadQueues(filters);
              }}
              onReset={() => {
                setFilters(EMPTY_FILTERS);
                setApplied(EMPTY_FILTERS);
                reloadQueues(EMPTY_FILTERS);
              }}
            />
          </div>
        )}
        <section
          role="tabpanel"
          id="admin-panel"
          aria-labelledby={`admin-tab-${tab}`}
          className="min-w-0 rounded-2xl border border-ground-edge bg-ground-raised p-5 md:p-6"
        >
          {panel()}
        </section>
      </div>

      {payFor && (
        <MarkPaidDialog
          withdrawal={payFor}
          onCancel={() => setPayFor(null)}
          onConfirm={(feeCents) => {
            const w = payFor;
            setPayFor(null);
            void act(
              () => adminApi.markPaid(w.id, feeCents),
              (r) => {
                replaceWithdrawal(r.withdrawal);
                setNotice({ kind: "ok", text: resultText(r.result) });
              },
            );
          }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const go = confirm.go;
            setConfirm(null);
            go();
          }}
        />
      )}
    </>
  );
}

function MoreButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="mt-6 text-center">
      <button type="button" onClick={onClick} className={ghostButton}>
        {copy.admin.more}
      </button>
    </div>
  );
}
